/**
 * Model-facing result copy for every client tool. These strings are PROMPTS: the
 * model reads them as instructions, so a wording change is a behavior change and
 * needs the same care as changing the code. The per-tool modules own dispatch and
 * data logic and call in here for the words.
 */
import { toStandardPageLabel } from '../global-ai-assistant-standard-page-match';
import type {
  ChartDataDigest,
  FilterResolutionDigest,
} from '../turn-outcome-digest';
import {
  chartSummary,
  filterSummary,
  NATURAL_METRIC_NAME_INSTRUCTION,
} from '../turn-outcome-digest';
import type { ClientToolResult } from './types';

/**
 * Keep this semantically aligned with the loop, page-gen, and terminal
 * offer-tool language policies. The reminder sits here because this result is
 * injected after the main loop prompt has already been built.
 */
const NATURAL_LANGUAGE_NARRATION_INSTRUCTION =
  'Write concrete, natural business English and name the relevant workforce directly, such as "Google\'s workforce" or "engineers in London". If no concise name is available, use "employees matching the request" or "the selected companies". Prefer short sentences that put the named people or companies in the main clause instead of attaching the requested filters after the figure; use a clear pronoun for supporting facts when possible. Avoid internal analytics jargon, generic labels, and vague abstractions.';

/**
 * Parse failure on a model-authored tool input: name what was malformed so the
 * model self-corrects on its next step (the same self-correction contract the
 * artifact-not-found errors use). Non-terminal — the loop feeds it back.
 */
export function malformedToolInputResult(
  name: string,
  issues: string
): ClientToolResult {
  return {
    resultForModel: `ERROR: invalid input for ${name} — ${issues}. Correct the argument(s) and call ${name} again with a valid input.`,
  };
}

/**
 * NO-OP result a tool returns when the turn's abort signal fired while one of
 * its awaited fetches was in flight (see ClientToolContext.signal). Returned
 * INSTEAD of running the rest of the tool body, so an aborted turn performs no
 * side effects — no transcript appends, no offer wiring — after the user
 * stopped it or the engine unmounted.
 *
 * Deliberately NOT `ERROR`-prefixed: an abort is a cancellation, not a tool
 * failure — the prefix would fire the loop's failure telemetry and read as a
 * failed outcome to `isClientToolFailure` consumers. And deliberately NOT
 * `endsTurn`: ending the turn here would surface the round's streamed
 * narration as the answer (a transcript write on a dead turn); returning
 * non-terminal lets the loop's next step hit the aborted signal and unwind
 * through the runner's silent abort handling instead. The model never acts on
 * this text — the next step never reaches it.
 */
export const turnAbortedNoOpResult: ClientToolResult = {
  resultForModel:
    'CANCELLED: the turn was stopped before this data arrived. Do not answer, do not retry, and take no further action.',
};

// --- navigate ---------------------------------------------------------------

export function navigateUnknownPageResult(optionId: string): ClientToolResult {
  // Not a real page — non-terminal error so the model can recover and
  // never claims a phantom navigation.
  return {
    resultForModel: `ERROR: ${optionId} is not a known page; do not claim to have navigated.`,
  };
}

export function navigateConfirmResult(pageName: string): ClientToolResult {
  return {
    resultForModel: `Presented a confirmation to open ${pageName}; the user must click to navigate. Tell the user you can take them to ${pageName} and to click the button to confirm. Do not claim to have navigated.`,
    endsTurn: true,
  };
}

// --- apply_filters ----------------------------------------------------------

export function applyFiltersMissingArtifactResult(
  filterArtifactId: string
): ClientToolResult {
  return {
    resultForModel: `ERROR: no filter artifact (${filterArtifactId}) — call search_filters first to resolve filters, then apply_filters.`,
  };
}

/**
 * Same-page recovery guard: the route moved between the model choosing the
 * filters and the tool running, so applying them would filter a page the user
 * did not ask about.
 */
export function applyFiltersPageChangedResult(
  expectedPageOptionId: string
): ClientToolResult {
  return {
    resultForModel: `ERROR: the open page changed before the filters could be applied, so no filters were applied. The intended destination was ${expectedPageOptionId}; offer or navigate to that destination normally instead of filtering the page now on screen.`,
  };
}

export function applyFiltersNoneResolvedResult(
  digest: FilterResolutionDigest
): ClientToolResult {
  return {
    resultForModel: `ERROR: no resolved filters were applied to the page.\n${filterSummary(digest)}\n\nDo not claim the page was filtered or that anything is being applied. Briefly name any UNRESOLVED filters and end the turn.`,
  };
}

export function applyFiltersAppliedResult(
  digest: FilterResolutionDigest
): ClientToolResult {
  // Optimistic apply (DP4): the command is enqueued, not confirmed — keep
  // the narration to what the artifact proves and present-progressive.
  // A geography-taxonomy note, when the artifact carries one, is appended by
  // the registry (`withGeoTaxonomyNote`) — and only to a non-ERROR result, so
  // the none-applied path above never discloses a substitution it didn't make.
  return {
    resultForModel: `${filterSummary(digest)}\n\nThe filters are being applied to the page now (they land within a moment) — say you are applying them, and describe WHAT was applied from the APPLIED list (and call out anything UNRESOLVED). Do not claim to be waiting, and do not describe the page's resulting data (you have not seen it).`,
  };
}

// --- shared single-value answer copy (render_chart's answerAsText branch) ---

/**
 * Build the model result for a single-value answer (NO chart card shown):
 * `render_chart`'s answerAsText branch runs its generated config headlessly
 * and hands the digest summary here for the words.
 */
export function answerAsTextResult(
  summary: string,
  standardPagePath: string | undefined
): ClientToolResult {
  const standardPageLabel = standardPagePath
    ? toStandardPageLabel(standardPagePath)
    : undefined;
  const leadInstruction =
    'LEADS with the asked figure in one grounded sentence (use the display-formatted latestValue / topCategories exactly, but do not expose internal labels; name only entities present in the data)';
  // The offer is optional; when made it must go through offer_standard_page,
  // which alone stamps the [](offer:page) marker onto THIS answer's chart. A
  // free-text marker resolves against `lastRenderedChartId`
  // (use-global-ai-turn-runner.ts): nothing here, or an EARLIER chart.
  if (standardPageLabel) {
    return {
      resultForModel: `${summary}\n\nENTITY CHECK FIRST: if the data above belongs to a DIFFERENT entity than the user asked about (e.g. filtersApplied is missing the asked company/industry/geography/role/skill/activity and topCategories/title name some other company — the resolver failed and the fetch ran on defaults), do NOT present these figures at all: say you couldn't resolve <the asked entity> right now, suggest retrying, and END the turn. A default-company figure presented as the asked company's answer is a wrong answer (seen live: "sentiment at Tesla" answered with Workday's 0.51). Otherwise: this is a single-value answer — NO chart is shown to the user, and there is a matching standard page for it: ${standardPageLabel}. The page offer is OPTIONAL: a grounded figure stated in plain text is a complete answer and you may end the turn there. IF you offer the page, it MUST go through offer_standard_page — a [<page name>](offer:page) marker you write in free text has no chart to resolve against and renders as dead plain text. Call it ONCE, with a narration that: (1) ${leadInstruction}; (2) adds one or two supporting facts drawn ONLY from the data above — the trend between the dates shown, or a supporting metric; express changes in rate metrics in percentage points, never relative percent; (3) ends with a SHORT, GENERIC reason to visit the page, ENDING on the marker [${standardPageLabel}](offer:page). Never bury the lead, never state a value not present above, never more than two supporting facts, never describe specific page contents beyond its name. ${NATURAL_LANGUAGE_NARRATION_INSTRUCTION} Refer to each metric by its plain name ONLY — never explain what a metric includes or how it is calculated (no "including bonuses"-style glosses; you do not know the methodology). Do not leak internal labels or phrases such as "latestValue", "topCategories", "unitHint", "supporting metrics", or "with the same filters"; translate them into natural language.`,
    };
  }
  return {
    resultForModel: `${summary}\n\nENTITY CHECK FIRST: if the data above belongs to a DIFFERENT entity than the user asked about (resolver failure — the fetch ran on defaults), do NOT present these figures: say you couldn't resolve the asked entity right now and END the turn. Otherwise: this is a single-value answer — NO chart is shown to the user. Reply with ONE short paragraph (2–3 sentences): ${leadInstruction}, then one or two supporting facts drawn ONLY from the data above (the trend between the dates shown, or a supporting metric; percentage points for rate changes, never relative percent), and you MAY add ONE short suggested follow-up grounded in a metric you can actually show (e.g. the growth trend, or a breakdown) — a grounded figure alone is a complete answer. Never bury the lead; never state a value not present above; never more than two supporting facts. ${NATURAL_LANGUAGE_NARRATION_INSTRUCTION} Refer to each metric by its plain name ONLY — never explain what a metric includes or how it is calculated (no "including bonuses"-style glosses; you do not know the methodology). Do not leak internal labels or phrases such as "latestValue", "topCategories", "unitHint", "supporting metrics", or "with the same filters"; translate them into natural language. Do NOT refer to "the chart". Then end your turn.`,
  };
}

// --- render_chart -----------------------------------------------------------

export function renderChartMissingArtifactResult(
  configArtifactId: string
): ClientToolResult {
  return {
    resultForModel: `ERROR: chart config artifact ${configArtifactId} not found`,
  };
}

export function renderChartFollowUpPageOption(
  standardPageLabel: string
): string {
  return `offer:page — open ${standardPageLabel} (shows this question's data with its filters applied); wrap the page name: [${standardPageLabel}](offer:page)`;
}

export function renderChartFollowUpInstruction(
  followUps: string[],
  standardPageLabel: string | undefined
): string {
  return followUps.length
    ? `\n\nAfter the grounded sentence you MAY end your narration by offering one of the applicable follow-ups below as an inline link when it adds to the chart the user is looking at (optional — a grounded description of the chart is already a complete answer): a short generic reason FIRST, then the clickable phrase wrapped in the named marker as the LAST thing in the clause (e.g. "the full picture lives on one page — [${standardPageLabel ?? 'Company Overview'}](offer:page)"). Use ONLY these marker schemes, do not invent others:\n- ${followUps.join('\n- ')}`
    : '';
}

export function renderChartDisplayedResult(
  summary: string,
  followUpInstruction: string
): ClientToolResult {
  return {
    resultForModel: `${summary}\n\nThe chart is now displayed to the user — this fulfils the request for it. Do NOT generate or render this same chart again. Narrate ONLY what the data above actually shows: name only the entities/categories present in this chart (per its title, latestValue, and topCategories) and the figures shown. Do NOT name any company, industry, geography, role, skill, activity, or value that is not in the data above, even if the user's request named it — if the chart shows different or fewer entities than asked (e.g. peer companies or a region rollup instead of the exact items requested), describe what is actually on screen rather than restating the request. Give a brief grounded sentence, then end your turn; call offer_report LAST only if a prebuilt report clearly matches, and render another chart only if the user asked for a different one.${followUpInstruction}`,
  };
}

// --- create_custom_page -----------------------------------------------------

export const createCustomPageOfferedResult: ClientToolResult = {
  resultForModel:
    'Presented a "Build custom dashboard" button; the user must click it to build and open the dashboard. Tell the user you can put that dashboard together and to click the button to build it. Do NOT claim the page was built or opened.',
  endsTurn: true,
};

// Expiry is stronger than a timeout: the destination page never picked
// the command up, so nothing was built at all. "May be incomplete"
// would understate it. Note the user IS on a real page — the chip click
// created an empty draft and navigated them there before the build was
// requested — so the copy says the page is empty, not that it's absent.
export const createCustomPageExpiredResult: ClientToolResult = {
  resultForModel:
    'ERROR: the dashboard build was never picked up by the destination page (the command expired). The build did NOT start and the page you are on is still empty — tell the user the build did not start and that they can try again. Do not claim the dashboard was built or is still building.',
};

export const createCustomPageUnconfirmedResult: ClientToolResult = {
  resultForModel:
    'ERROR: the dashboard build did not confirm in time; it may be incomplete — do not claim the dashboard was built.',
};

// The build confirmed but the page's widget data hasn't settled yet, so
// we have nothing concrete to ground on — do not invent specific charts.
export const createCustomPageNoDigestsResult: ClientToolResult = {
  resultForModel:
    'The dashboard was created, but no chart data has settled yet — do not describe specific charts or claim the page covers any particular metric; just say the dashboard is being built.',
};

// Partial coverage (a collector tier fired before every widget settled):
// the absolute "ONLY charts on the page" contract would be wrong-by-
// omission — a chart that exists but hasn't settled would be denied.
// Tell the model exactly what settled and what's still loading (DP5).
export function createCustomPagePartialResult(
  expectedCount: number,
  digests: ChartDataDigest[]
): ClientToolResult {
  return {
    resultForModel: `Created the dashboard with ${expectedCount} charts; data has settled for ${digests.length} of them so far (the rest are still loading):\n${digests.map(chartSummary).join('\n---\n')}\n\nGive a brief grounded summary (1–2 sentences) leading with the concrete figures above, referring to charts by their titles, and mention the remaining charts are still loading. Do NOT invent contents for charts not listed — never name their metrics or values. Frame it as a NEWLY BUILT dashboard, not an update. ${NATURAL_METRIC_NAME_INSTRUCTION} This fulfils the request; then end your turn — call offer_report LAST only if a prebuilt report clearly matches.`,
  };
}

// Enumerate the ACTUAL widgets the build produced and constrain the
// narration to them — CREATE semantics ("created", not "updated"). Without
// this, the model narrates a generic "comprehensive workforce dashboard"
// template and invents sections that aren't on the page.
export function createCustomPageBuiltResult(
  digests: ChartDataDigest[]
): ClientToolResult {
  const builtWidgetLabel =
    digests.length === 1 ? '1 widget' : `${digests.length} widgets`;
  return {
    resultForModel: `Created the dashboard with ${builtWidgetLabel}. These are the ONLY charts on the page — narrate ONLY these:\n${digests.map(chartSummary).join('\n---\n')}\n\nGive a brief grounded summary (1–2 sentences) that refers to these charts by their titles and leads with the concrete figures above. Do NOT mention any metric, theme, or section that is not one of the charts listed — e.g. do not claim the dashboard covers compensation, sentiment, demographics, attrition, or transitions unless a chart above is actually about it. Frame it as a NEWLY BUILT dashboard, not an update. ${NATURAL_METRIC_NAME_INSTRUCTION} This fulfils the request; then end your turn — call offer_report LAST only if a prebuilt report clearly matches.`,
  };
}

// --- offer_page_or_custom ---------------------------------------------------

export const offerPageOrCustomBuildOnlyResult: ClientToolResult = {
  resultForModel:
    'Presented a "Build custom dashboard" button (the templated page id did not resolve). Tell the user you can build that dashboard and to click the button; do NOT claim any page was opened or built.',
  endsTurn: true,
};

export const offerPageOrCustomFiltersUnresolvedResult: ClientToolResult = {
  resultForModel:
    'Presented only a "Build custom dashboard" button. No page link was created: the filters for this offer did not resolve, and the page would have opened on its default filters. Tell the user the specific filter could not be resolved and that you can build a custom dashboard; do NOT offer the page unfiltered, and do NOT claim any page was opened or built.',
  endsTurn: true,
};

export function offerPageOrCustomBothResult(
  pageName: string
): ClientToolResult {
  return {
    resultForModel: `Presented BOTH options: a button to open ${pageName}, and a "Build custom dashboard" button. Tell the user the ${pageName} page covers the core of their question, and that you can also build a custom dashboard for the full request — they click whichever they want. Do NOT claim either was opened or built.`,
    endsTurn: true,
  };
}

// --- offer_standard_page ----------------------------------------------------

export const offerStandardPageUnavailableResult: ClientToolResult = {
  resultForModel:
    'ERROR: no page link can back this answer, so nothing was shown. Give the answer in plain text with no page link and no [](offer:page) marker. Do not claim a link was offered or explain why there is none.',
};

// The destination IS the page on screen, so the offer link was stripped back
// to plain text and only the figure remains.
export const offerStandardPageSamePageResult: ClientToolResult = {
  resultForModel:
    'The user is already on the matching standard page, so the self-link was removed. Do not offer or navigate to the page again; end your turn.',
  endsTurn: true,
};

export const offerStandardPageResult: ClientToolResult = {
  resultForModel:
    'Presented the single-value answer with an inline link to the standard page; the user clicks the link to open it. Do NOT add more text or navigate — end your turn.',
  endsTurn: true,
};

// --- update_current_page ----------------------------------------------------

// See create_custom_page: an expired command was never claimed, so the
// page was not touched at all.
export const updateCurrentPageExpiredResult: ClientToolResult = {
  resultForModel:
    'ERROR: the page update was never picked up by the destination page (the command expired). The update did NOT start and the page is unchanged — tell the user the update did not start and that they can try again. Do not claim the page was updated or is still updating.',
};

export const updateCurrentPageUnconfirmedResult: ClientToolResult = {
  resultForModel:
    'ERROR: the page update did not confirm in time; it may be incomplete — do not claim the page was updated.',
};

// The command confirmed but the page's widget data hasn't settled yet, so
// we have nothing concrete to ground on — do not invent specific charts.
export const updateCurrentPageNoDigestsResult: ClientToolResult = {
  resultForModel:
    'The page was updated, but no chart data has settled yet — do not describe specific charts or claim the page covers any particular metric; just say the page is being built.',
};

// Partial coverage (a collector tier fired before every widget settled):
// the absolute "ONLY charts on the page" contract would be wrong-by-
// omission — a chart that exists but hasn't settled would be denied.
// Tell the model exactly what settled and what's still loading (DP5).
export function updateCurrentPagePartialResult(
  expectedCount: number,
  digests: ChartDataDigest[]
): ClientToolResult {
  return {
    resultForModel: `The page has ${expectedCount} charts; data has settled for ${digests.length} of them so far (the rest are still loading):\n${digests.map(chartSummary).join('\n---\n')}\n\nGive a brief grounded summary (1–2 sentences) leading with the concrete figures above, referring to charts by their titles, and mention the remaining charts are still loading. Do NOT invent contents for charts not listed — never name their metrics or values. ${NATURAL_METRIC_NAME_INSTRUCTION} This fulfils the request; then end your turn — call offer_report LAST only if a prebuilt report clearly matches.`,
  };
}

// Enumerate the ACTUAL widgets now on the page and constrain the narration
// to them. Without this, the model narrates a generic "comprehensive
// workforce dashboard" template and invents sections that aren't on the
// page (e.g. claiming compensation/sentiment/demographics charts that were
// never generated) — the page-build analogue of the single-chart
// guardrail in render_chart.
export function updateCurrentPageUpdatedResult(
  digests: ChartDataDigest[]
): ClientToolResult {
  const widgetLabel =
    digests.length === 1 ? '1 widget' : `${digests.length} widgets`;
  return {
    resultForModel: `The page now has ${widgetLabel}. These are the ONLY charts on the page — narrate ONLY these:\n${digests.map(chartSummary).join('\n---\n')}\n\nGive a brief grounded summary (1–2 sentences) that refers to these charts by their titles and leads with the concrete figures above. Do NOT mention any metric, theme, or section that is not one of the charts listed — e.g. do not claim the page covers compensation, sentiment, demographics, attrition, or transitions unless a chart above is actually about it. ${NATURAL_METRIC_NAME_INSTRUCTION} This fulfils the request; then end your turn — call offer_report LAST only if a prebuilt report clearly matches.`,
  };
}

// --- offer_report -----------------------------------------------------------

// Zero chips: ending the turn would dead-end with nothing shown — return
// a non-terminal result and let the model recover/continue.
export const offerReportNoMatchResult: ClientToolResult = {
  resultForModel: 'No matching report could be offered.',
};

/** Why a same-page report ride-along could not apply its filters. */
export function offerReportSamePageUnresolvedReason(
  digest: FilterResolutionDigest
): string {
  return `no resolved filters were applied. ${filterSummary(digest)}`;
}

export function offerReportResult(params: {
  reportNames: string[];
  pageOfferedName: string | undefined;
  pageTriedButUnresolved: boolean;
  pageFiltersUnresolved: boolean;
  /** The page half of the ride-along was the page already on screen. */
  samePageRideAlong: boolean;
  samePageFilterDigest: FilterResolutionDigest | undefined;
  samePageFilterError: string | undefined;
}): ClientToolResult {
  const {
    reportNames,
    pageOfferedName,
    pageTriedButUnresolved,
    pageFiltersUnresolved,
    samePageRideAlong,
    samePageFilterDigest,
    samePageFilterError,
  } = params;
  return {
    resultForModel: `Offered ${reportNames.length} report(s): ${reportNames.join(', ')} — the explanation with the inline link(s) is already shown to the user.${
      samePageFilterDigest
        ? ` The matching page was already open, so its page offer was removed and these filters are being applied now:\n${filterSummary(samePageFilterDigest)}`
        : ''
    }${
      samePageFilterError
        ? ` The matching page was already open, but its filters were NOT applied: ${samePageFilterError}. Do not claim they were applied.`
        : ''
    }${
      pageOfferedName !== undefined
        ? ` ALSO presented a button to open ${pageOfferedName}; the user clicks whichever they want. Do NOT claim the page was opened.`
        : ''
    }${
      pageTriedButUnresolved
        ? ' NOTE: the page you tried to offer alongside did not resolve, so only the report(s) are shown — do not mention a page.'
        : ''
    }${
      pageFiltersUnresolved && !samePageRideAlong
        ? ' NOTE: the filters for the page offer did NOT resolve, so no page link was created — tell the user the specific filter could not be resolved, and do not offer the page unfiltered.'
        : ''
    }${
      samePageRideAlong && pageFiltersUnresolved
        ? ' NOTE: the matching page was already open, but its filter artifact did not resolve; no filters were applied and no page link was shown.'
        : ''
    } Do NOT repeat or re-describe the report(s); end your turn.`,
    endsTurn: true,
  };
}
